import { LocationController } from "@/app/ui/controllers/LocationController";

export type DistrictEntityType = "parent" | "branch" | "partner";
export type TaxIdInputMode = "alphanumeric" | "digit";

export interface DistrictFieldRules {
  countryCode: string;
  entityType: DistrictEntityType;
  phoneDialCode: string;
  currencyLabel: string;
  currencyCode: string;
  taxIdMaxLength: number;
  taxIdMinLength: number;
  taxIdInputMode: TaxIdInputMode;
  nikIdMaxLength: number;
  nikIdMinLength: number;
  nikIdInputMode: TaxIdInputMode | null;
  showIndonesiaTaxBlock: boolean;
  applyNpwpMask: boolean;
}

type DistrictDetail = {
  countryCode?: string | null;
  country_code?: string | null;
};

export type DistrictValue =
  | number
  | string
  | {
      id?: number | string | null;
      code?: string | null;
      districtCode?: string | null;
    }
  | null
  | undefined;

export interface DistrictLookupOptions {
  notSaveDetail?: boolean;
}

const DEFAULT_ENTITY_TYPE: DistrictEntityType = "parent";
const ID_COUNTRY_CODE = "ID";
const MY_COUNTRY_CODE = "MY";

const CURRENCY_LABEL: Record<string, string> = {
  [MY_COUNTRY_CODE]: "RM",
  [ID_COUNTRY_CODE]: "Rp",
};

const COUNTRY_CURRENCY_MAP: Record<string, string> = {
  MY: "MYR",
  ID: "IDR",
};

const DEFAULT_CURRENCY = "IDR";

export function getCurrencyByCountryCode(countryCode: string): string {
  return COUNTRY_CURRENCY_MAP[countryCode.toUpperCase()] ?? DEFAULT_CURRENCY;
}

export function normalizeDistrictCountryCode(code?: string | null): string {
  const normalized = (code ?? "").trim().toUpperCase();
  return normalized === MY_COUNTRY_CODE ? MY_COUNTRY_CODE : ID_COUNTRY_CODE;
}

export function isMalaysiaDistrict(code?: string | null): boolean {
  return normalizeDistrictCountryCode(code) === MY_COUNTRY_CODE;
}

const COUNTRY_DIAL_CODES: Record<string, string> = {
  MY: "+60",
  ID: "+62",
};

const DEFAULT_DIAL_CODE = "+62";

export function getPhoneDisplayCode(countryCode: string): string {
  return COUNTRY_DIAL_CODES[countryCode.toUpperCase()] ?? DEFAULT_DIAL_CODE;
}

function resolveEntityType(entityType?: DistrictEntityType): DistrictEntityType {
  return entityType ?? DEFAULT_ENTITY_TYPE;
}

function buildMalaysiaRules(entityType: DistrictEntityType): DistrictFieldRules {
  const countryCode = MY_COUNTRY_CODE;
  return {
    countryCode,
    entityType,
    phoneDialCode: getPhoneDisplayCode(countryCode),
    currencyLabel: CURRENCY_LABEL[countryCode],
    currencyCode: getCurrencyByCountryCode(countryCode),
    taxIdMaxLength: 13,
    taxIdMinLength: 0,
    taxIdInputMode: "alphanumeric",
    nikIdMaxLength: 12,
    nikIdMinLength: 0,
    nikIdInputMode: "alphanumeric",
    showIndonesiaTaxBlock: false,
    applyNpwpMask: false,
  };
}

function buildIndonesiaRules(entityType: DistrictEntityType): DistrictFieldRules {
  const countryCode = ID_COUNTRY_CODE;
  const base = {
    countryCode,
    entityType,
    phoneDialCode: getPhoneDisplayCode(countryCode),
    currencyLabel: CURRENCY_LABEL[countryCode],
    currencyCode: getCurrencyByCountryCode(countryCode),
    showIndonesiaTaxBlock: entityType === "parent",
    applyNpwpMask: entityType === "partner",
  };

  if (entityType === "parent") {
    return {
      ...base,
      taxIdMaxLength: 16,
      taxIdMinLength: 15,
      taxIdInputMode: "digit",
      nikIdMaxLength: 16,
      nikIdMinLength: 15,
      nikIdInputMode: "digit",
    };
  }

  if (entityType === "branch") {
    return {
      ...base,
      taxIdMaxLength: 20,
      taxIdMinLength: 15,
      taxIdInputMode: "digit",
      nikIdMaxLength: 0,
      nikIdMinLength: 0,
      nikIdInputMode: null,
    };
  }

  return {
    ...base,
    taxIdMaxLength: 30,
    taxIdMinLength: 20,
    taxIdInputMode: "digit",
    nikIdMaxLength: 0,
    nikIdMinLength: 0,
    nikIdInputMode: null,
  };
}

// ini
export function getFieldRulesForDistrict(
  code?: string | null,
  entityType?: DistrictEntityType
): DistrictFieldRules {
  const resolvedEntityType = resolveEntityType(entityType);
  const countryCode = normalizeDistrictCountryCode(code);
  if (countryCode === MY_COUNTRY_CODE) {
    return buildMalaysiaRules(resolvedEntityType);
  }
  return buildIndonesiaRules(resolvedEntityType);
}

function stripNonAlphanumeric(value: string): string {
  return value.replace(/[^a-zA-Z0-9]/g, "");
}

function stripNonDigit(value: string): string {
  return value.replace(/\D/g, "");
}

function isEmptyValue(value: string): boolean {
  return value.trim().length === 0;
}

function isDigitOnly(value: string): boolean {
  return /^\d+$/.test(value);
}

export function formatTaxIdInput(value: string, rules: DistrictFieldRules): string {
  if (rules.taxIdInputMode === "alphanumeric") {
    return stripNonAlphanumeric(value).slice(0, rules.taxIdMaxLength);
  }
  return stripNonDigit(value).slice(0, rules.taxIdMaxLength);
}

export function validateTaxIdValue(value: string, rules: DistrictFieldRules): boolean {
  const normalized =
    rules.taxIdInputMode === "alphanumeric"
      ? stripNonAlphanumeric(value)
      : stripNonDigit(value);

  if (isEmptyValue(normalized)) {
    return rules.taxIdMinLength === 0;
  }

  if (rules.taxIdInputMode === "alphanumeric") {
    return (
      normalized.length >= rules.taxIdMinLength &&
      normalized.length <= rules.taxIdMaxLength
    );
  }

  if (!isDigitOnly(normalized)) {
    return false;
  }

  return (
    normalized.length >= rules.taxIdMinLength &&
    normalized.length <= rules.taxIdMaxLength
  );
}

export function formatNikIdInput(value: string, rules: DistrictFieldRules): string {
  if (!rules.nikIdInputMode) {
    return value;
  }
  if (rules.nikIdInputMode === "alphanumeric") {
    return stripNonAlphanumeric(value).slice(0, rules.nikIdMaxLength);
  }
  return stripNonDigit(value).slice(0, rules.nikIdMaxLength);
}

export function validateNikIdValue(value: string, rules: DistrictFieldRules): boolean {
  if (!rules.nikIdInputMode || rules.nikIdMaxLength === 0) {
    return true;
  }

  const normalized =
    rules.nikIdInputMode === "alphanumeric"
      ? stripNonAlphanumeric(value)
      : stripNonDigit(value);

  if (isEmptyValue(normalized)) {
    return rules.nikIdMinLength === 0;
  }

  if (rules.nikIdInputMode === "alphanumeric") {
    return (
      normalized.length >= rules.nikIdMinLength &&
      normalized.length <= rules.nikIdMaxLength
    );
  }

  if (!isDigitOnly(normalized)) {
    return false;
  }

  return (
    normalized.length >= rules.nikIdMinLength &&
    normalized.length <= rules.nikIdMaxLength
  );
}

export function formatStoredPhoneForDisplay(
  storedPhone: string | null | undefined,
  rules: DistrictFieldRules
): string {
  const raw = (storedPhone ?? "").trim();
  if (!raw) {
    return "";
  }
  if (raw.startsWith("+")) {
    return raw;
  }

  const dialDigits = rules.phoneDialCode.replace(/\D/g, "");
  const digits = raw.replace(/\D/g, "").replace(/^0+/, "");
  if (digits.startsWith(dialDigits)) {
    return `${rules.phoneDialCode}${digits.slice(dialDigits.length)}`;
  }
  return `${rules.phoneDialCode}${digits}`;
}

export function formatStoredTaxIdForDisplay(
  storedTaxId: string | null | undefined,
  rules: DistrictFieldRules
): string {
  const raw = (storedTaxId ?? "").trim();
  if (!raw) {
    return "";
  }
  if (rules.taxIdInputMode === "alphanumeric") {
    return raw;
  }
  if (rules.entityType === "branch") {
    return raw.replace(/[.-]/g, "");
  }
  return raw;
}

export function extractDistrictId(districtValue: DistrictValue): number | null {
  if (districtValue == null || districtValue === "") {
    return null;
  }

  if (typeof districtValue === "number" && Number.isFinite(districtValue)) {
    return districtValue;
  }

  if (typeof districtValue === "string") {
    const trimmed = districtValue.trim();
    if (!trimmed) {
      return null;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (typeof districtValue === "object") {
    return extractDistrictId(districtValue.id ?? null);
  }

  return null;
}

export function extractDistrictCode(districtValue: DistrictValue): string | null {
  if (districtValue == null || districtValue === "") {
    return null;
  }

  if (typeof districtValue === "string") {
    const trimmed = districtValue.trim();
    if (!trimmed || Number.isFinite(Number(trimmed))) {
      return null;
    }
    return trimmed;
  }

  if (typeof districtValue === "object") {
    const code = districtValue.code ?? districtValue.districtCode ?? null;
    if (code == null) {
      return null;
    }
    const trimmed = String(code).trim();
    return trimmed || null;
  }

  return null;
}

const DEFAULT_LOOKUP_OPTIONS: Required<DistrictLookupOptions> = {
  notSaveDetail: true,
};

function readCountryCode(detail?: DistrictDetail | null): string | null {
  if (!detail) {
    return null;
  }
  return detail.countryCode ?? detail.country_code ?? null;
}

export async function fetchDistrictCountryCode(
  lookup: { districtId?: number | null; districtCode?: string | null },
  options: DistrictLookupOptions = {}
): Promise<string> {
  const districtId = lookup.districtId ?? null;
  const districtCode = lookup.districtCode ?? null;

  if (districtId == null && !districtCode) {
    return normalizeDistrictCountryCode(null);
  }

  const resolvedOptions = {
    ...DEFAULT_LOOKUP_OPTIONS,
    ...options,
  };

  try {
    if (districtId != null) {
      const response = await LocationController.getDetailDistrictTariff({
        id: districtId,
        notSaveDetail: resolvedOptions.notSaveDetail,
      });

      const detail =
        (response as DistrictDetail | undefined) ??
        (LocationController.districtDetailTariff as DistrictDetail | undefined);

      return normalizeDistrictCountryCode(readCountryCode(detail));
    }

    const response = await LocationController.getDetailDistrictByDistrictCode({
      districtCode: districtCode as string,
    });

    const detail =
      (response as DistrictDetail | undefined) ??
      (LocationController.districtDetail as DistrictDetail | undefined);

    return normalizeDistrictCountryCode(readCountryCode(detail));
  } catch {
    return normalizeDistrictCountryCode(null);
  }
}

export async function resolveDistrictFieldRules(
  districtValue: DistrictValue,
  entityType?: DistrictEntityType,
  options: DistrictLookupOptions = {}
): Promise<DistrictFieldRules> {
  const districtId = extractDistrictId(districtValue);
  const districtCode = extractDistrictCode(districtValue);

  if (districtId == null && !districtCode) {
    return getFieldRulesForDistrict(null, entityType);
  }

  const countryCode = await fetchDistrictCountryCode(
    { districtId, districtCode },
    options
  );
  return getFieldRulesForDistrict(countryCode, entityType);
}